import simpleRestProvider from 'ra-data-simple-rest';
import { fetchUtils } from 'react-admin';
import authProvider from './authProvider';
import { API_URL } from './apiConfig';

const httpClient = (url, options = {}) => {
    // Ensure trailing slash before query string
    let apiUrl = url;
    const [path, query] = url.split('?');
    const normalizedPath = path.endsWith('/') ? path : `${path}/`;
    apiUrl = query ? `${normalizedPath}?${query}` : normalizedPath;

    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    // Add auth header
    const authHeader = authProvider.getAuthHeader();
    Object.keys(authHeader).forEach(key => {
        options.headers.set(key, authHeader[key]);
    });
    return fetchUtils.fetchJson(apiUrl, options);
};

const baseProvider = simpleRestProvider(API_URL, httpClient);

const dataProvider = {
    ...baseProvider,
    create: async (resource, params) => {
        // Handle file uploads
        if (params.data.featured_image && params.data.featured_image.rawFile) {
            const formData = new FormData();
            Object.keys(params.data).forEach(key => {
                if (key === 'featured_image') {
                    formData.append(key, params.data[key].rawFile);
                } else if (key === 'gallery_images') {
                    // Handle gallery_images as JSON
                    try {
                        const galleryData = typeof params.data[key] === 'string' ? 
                            JSON.parse(params.data[key]) : params.data[key];
                        formData.append(key, JSON.stringify(galleryData));
                    } catch (e) {
                        formData.append(key, JSON.stringify([]));
                    }
                } else {
                    formData.append(key, params.data[key]);
                }
            });
            
            const url = `${API_URL}/${resource}/`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: formData,
                headers: new Headers({
                    // Don't set Content-Type, let the browser set it with the boundary
                }),
            });
            return {
                data: { ...params.data, id: json.id },
            };
        }
        return baseProvider.create(resource, params);
    },
    update: async (resource, params) => {
        // Handle file uploads
        if (params.data.featured_image && params.data.featured_image.rawFile) {
            const formData = new FormData();
            Object.keys(params.data).forEach(key => {
                if (key === 'featured_image') {
                    formData.append(key, params.data[key].rawFile);
                } else if (key === 'gallery_images') {
                    // Handle gallery_images as JSON
                    try {
                        const galleryData = typeof params.data[key] === 'string' ? 
                            JSON.parse(params.data[key]) : params.data[key];
                        formData.append(key, JSON.stringify(galleryData));
                    } catch (e) {
                        formData.append(key, JSON.stringify([]));
                    }
                } else {
                    formData.append(key, params.data[key]);
                }
            });
            
            const url = `${API_URL}/${resource}/${params.id}/`;
            const { json } = await httpClient(url, {
                method: 'PUT',
                body: formData,
                headers: new Headers({
                    // Don't set Content-Type, let the browser set it with the boundary
                }),
            });
            return {
                data: { ...params.data, id: json.id },
            };
        }
        return baseProvider.update(resource, params);
    },
    getList: async (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify(params.filter),
        };
        const url = `${API_URL}/${resource}/?${fetchUtils.queryParameters(query)}`;
        const { json } = await httpClient(url);
        return {
            data: json.results,
            total: json.count,
        };
    },
    getMany: async (resource, params) => {
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        };
        const url = `${API_URL}/${resource}/?${fetchUtils.queryParameters(query)}`;
        const { json } = await httpClient(url);
        return {
            data: json.results,
        };
    },
    getOne: async (resource, params) => {
        const url = `${API_URL}/${resource}/${params.id}/`;
        const { json } = await httpClient(url);
        // Some APIs return the object directly, others wrap in 'result'
        return {
            data: json.result ? json.result : json,
        };
    },
    getManyReference: async (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const filter = { ...params.filter, [params.target]: params.id };
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify(filter),
        };
        const url = `${API_URL}/${resource}/?${fetchUtils.queryParameters(query)}`;
        const { json } = await httpClient(url);
        return {
            data: json.results,
            total: json.count,
        };
    },
};

export default dataProvider;